import { Image } from "@heroui/react";
import { FocusScope } from "@react-aria/focus";
import clsx from "clsx";
import { APP_CLINIC, DOCUMENT_PRIVACY_POLICY } from "core/consts";
import {
  ENV_DEMO,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
  getStaticAsset,
  isAcpApp,
  isDemoEnv,
  isPreprodEnv,
  isProdEnv,
  isReceiverApp,
  isSenderApp,
} from "core/model/config";
import {
  activateEnvSwitch,
  isDeveloperMode,
} from "core/model/utils/featureFlags";
import { capitaliseFirst } from "core/model/utils/strings";
import { Env } from "core/types";
import {
  Banner,
  Button,
  ConnectedInput,
  ConnectedPasswordInput,
  Link,
  Spinner,
} from "ds/ui";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoWithNamePath } from "ds_legacy/components/RecareLogo";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { ArrowLeftIcon, LockIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "translations";
import { setCurrentAppOptions } from "../CareproviderOnboardingLoginPage/LoginSuggestions";
import {
  LoginErrorState,
  LoginView,
} from "../CareproviderOnboardingLoginPage/useHandleLogin";
import { loginPage, LoginPageVariants } from "./classes";

export type LoginPageV2Props = LoginPageVariants & {
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isEmailInputLoading?: boolean;
  isLoading: boolean;
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  onChangeEmail?: (value: string) => void;
  onSubmit: () => void;
  otherAppUrl?: string;
  resetError: () => void;
};

const ENVIRONMENTS = [
  ENV_STAGING,
  ENV_PREPROD,
  ENV_DEMO,
  ENV_PRODUCTION,
] as const;

export function LoginPageV2({
  app = APP_CLINIC,
  challengeTimestamp,
  contactUsUrl,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isEmailInputLoading,
  isLoading,
  isSingleSignOn,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onChangeEmail,
  onSubmit,
  otherAppUrl,
  resetError,
}: LoginPageV2Props) {
  const isDemo = isDemoEnv(env);
  const isProd = isProdEnv(env);
  const isPreprod = isPreprodEnv(env);
  const classes = loginPage({ app, env });
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();
  const navigate = useNavigate();
  const appOptions = setCurrentAppOptions({ currentApp: app, env });
  const isLogin = loginView === "login";
  const isChallenge = loginView === "challenge";

  return (
    <div className={classes.page()}>
      {isDeveloperMode && (
        <div className={classes.devToolContainer()}>
          {activateEnvSwitch && (
            <div
              className={clsx(
                classes.envSwitcher(),
                "rounded-md border border-primary bg-white p-2",
              )}
            >
              {ENVIRONMENTS.map((envOption) => (
                <Button
                  key={envOption}
                  color="primary"
                  id={`env_switch_${envOption}`}
                  isDisabled={isLoading}
                  size="sm"
                  variant={env === envOption ? "solid" : "light"}
                  onPress={() => navigate({ search: `?env=${envOption}` })}
                >
                  {capitaliseFirst(envOption)}
                </Button>
              ))}
            </div>
          )}

          {activateEnvSwitch && (
            <div className={classes.appSuggestion()}>
              {appOptions.map(({ href, label }, index) => (
                <Link
                  key={index}
                  color="primary-dark"
                  href={href}
                  id={label}
                  size="sm"
                >
                  {label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={classes.container()}>
        <div className={classes.content()}>
          <div className={classes.header()}>
            <Image
              alt="Recare logo"
              src={getStaticAsset(getRecareLogoWithNamePath("master"))}
              width={160}
            />
          </div>
          <div className={classes.form()}>
            <div className={classes.formContent()}>
              {isChallenge && (
                <div>
                  <FocusScope autoFocus>
                    <Button
                      color="primary"
                      id="back_to_login"
                      size="sm"
                      startContent={<ArrowLeftIcon />}
                      variant="light"
                      onPress={onBackToLogin}
                    >
                      {translations.actions.back}
                    </Button>
                  </FocusScope>
                </div>
              )}
              <h1 className={classes.title()}>
                {(isPreprod || isProd) && isAcpApp(app) && (
                  <span className="font-normal">
                    {isProd
                      ? translations.acp.login.prefixProd
                      : translations.acp.login.prefixPreprod}{" "}
                    |{" "}
                  </span>
                )}
                {isDemo && (
                  <span className="font-normal">
                    {translations.login.loginPage.titleDemo} |{" "}
                  </span>
                )}
                {isLogin
                  ? isSenderApp(app)
                    ? translations.login.loginPage.titleSender
                    : isReceiverApp(app)
                    ? translations.login.loginPage.titleReceiver
                    : translations.login.loginPage.titleAcp
                  : translations.login.loginPage.titleAccountVerification}
              </h1>
              {isChallenge && (
                <p className="text-sm">
                  {translations.login.loginPage.accountVerificationInfo}
                </p>
              )}
              {isDemo && (
                <Banner
                  color="warning"
                  message={
                    <p>
                      {translations.login.loginPage.environmentInfo}{" "}
                      <Link
                        aria-label={externalLinkLabel(
                          translations.login.loginPage.productionLink,
                          "website",
                        )}
                        color="primary-dark"
                        href={goToProductionUrl}
                        id="go-to-prod"
                        size="sm"
                      >
                        {translations.login.loginPage.productionLink}
                      </Link>
                      .
                    </p>
                  }
                  size="sm"
                />
              )}
            </div>
            <div className={classes.formContent()}>
              {loginErrors?.general && (
                <Banner
                  color="danger"
                  data-testid="login-error"
                  message={loginErrors.general}
                  role="alert"
                />
              )}
              {isChallenge && challengeTimestamp && (
                <p className="text-sm">
                  {translations.login.challengeScreen.infoTextTimestamp({
                    datetimestring: challengeTimestamp,
                  })}
                </p>
              )}
              <ConnectedInput
                autoComplete="email"
                endContent={
                  isEmailInputLoading ? (
                    <Spinner size="sm" testId="email-input-spinner" />
                  ) : undefined
                }
                errorMessage={loginErrors?.email}
                isInvalid={!!loginErrors?.email}
                label={translations.login.loginPage.email}
                name="email"
                placeholder={translations.login.loginPage.emailPlaceholder}
                required
                sideMutation={() => resetError()}
                type={isLogin ? "email" : "hidden"}
                onValueChange={onChangeEmail}
              />
              {isSingleSignOn && (
                <div
                  aria-live="polite"
                  className="flex max-w-fit items-center space-x-2 rounded-md border border-success bg-success-light py-1.5 pl-3 pr-5 text-success-dark"
                  role="alert"
                >
                  <LockIcon aria-hidden="true" size={14} />{" "}
                  <p className="text-sm">
                    {translations.login.loginPage.ssoInfo}
                  </p>
                </div>
              )}
              <ConnectedPasswordInput
                autoComplete={
                  isLogin && !isSingleSignOn ? "current-password" : "off"
                }
                elementName="password"
                errorMessage={loginErrors?.password}
                isInvalid={!!loginErrors?.password}
                label={translations.login.loginPage.password}
                placeholder={translations.login.loginPage.passwordPlaceholder}
                required
                sideMutation={() => resetError()}
                type={isLogin && !isSingleSignOn ? "password" : "hidden"}
              />
              <ConnectedInput
                autoComplete="off"
                errorMessage={loginErrors?.challenge}
                isInvalid={!!loginErrors?.challenge}
                label={translations.login.loginPage.verificationCode}
                name="challenge"
                placeholder={
                  translations.login.loginPage.verificationCodePlaceholder
                }
                required
                sideMutation={() => resetError()}
                type={isLogin ? "hidden" : "text"}
              />
              {isLogin && !isSingleSignOn && (
                <p>
                  <Link
                    color="primary-dark"
                    href={goToForgotPassword}
                    id="forgot-password"
                    size="sm"
                  >
                    {translations.login.loginPage.forgotPassword}
                  </Link>
                </p>
              )}
            </div>
            <div className="flex justify-between">
              <Button
                color="primary"
                fullWidth
                id={
                  isLogin
                    ? isSingleSignOn
                      ? "login_sso"
                      : "login"
                    : "login_challenge"
                }
                isLoading={isLoading}
                size="lg"
                type="submit"
                variant="solid"
                onPress={onSubmit}
              >
                {translations.login.loginPage.loginButton}
              </Button>
            </div>
          </div>
        </div>
        <div className={classes.footer()}>
          {isLogin && !isAcpApp(app) && (
            <div className={classes.footerLinks()}>
              <p>
                {translations.login.loginPage.contactInfo}{" "}
                <Link
                  aria-label={externalLinkLabel(
                    translations.login.loginPage.contactInfoLink,
                    "website",
                  )}
                  color="primary-dark"
                  href={contactUsUrl}
                  id="contact-us"
                  size="sm"
                >
                  {translations.login.loginPage.contactInfoLink}
                </Link>
              </p>
              <p>
                {isSenderApp(app)
                  ? translations.login.loginPage.receiverInfo
                  : translations.login.loginPage.clinicInfo}{" "}
                <Link
                  aria-label={externalLinkLabel(
                    translations.login.clinic.clickHere,
                    "website",
                  )}
                  color="primary-dark"
                  href={otherAppUrl}
                  id="switch-app"
                  size="sm"
                >
                  {isSenderApp(app)
                    ? translations.login.loginPage.receiverInfoLink
                    : translations.login.loginPage.clinicLink}
                </Link>
              </p>
            </div>
          )}
          <hr className={classes.footerDivider()} />
          <p>
            {translations.login.loginPage.privacyPolicyInfo}{" "}
            <Link
              aria-label={externalLinkLabel(
                translations.login.privacyPolicy,
                "pdf",
              )}
              color="primary-dark"
              href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
              id="privacy-policy"
              size="sm"
              target="_blank"
            >
              {translations.login.privacyPolicy}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
